import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { logoutJWT } from '../auth/auth-client';
import { ApiResponse } from '../types';
import config from '../utils/config';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.dashboardApi,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          this.handleUnauthorized();
        }
        return Promise.reject(error);
      }
    );
  }

  private getAuthToken(): string | null {
    // Get JWT token from localStorage
    return localStorage.getItem('auth_token');
  }

  private getCurrentOps(): string {
    // Get current ops from localStorage, fallback to 'gestor' if not set
    return localStorage.getItem('currentOps') || 'gestor';
  }

  private handleUnauthorized() {
    // Clear JWT token from localStorage
    logoutJWT();

    // Redirect to login page
    window.location.href = '/login';
  }

  async get<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    // Direct endpoint call for GET requests
    const response = await this.client.get(url, { params });
    return this.transformResponse(response);
  }

  async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    // For POST requests, automatically add ops parameter if not present
    const postData = data ? { ...data } : {};

    // Add ops parameter if not already present
    if (!postData.ops) {
      postData.ops = this.getCurrentOps();
    }

    // Direct endpoint call
    const response = await this.client.post(url, postData);
    return this.transformResponse(response);
  }

  async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    // For PUT requests, automatically add ops parameter if not present
    const putData = data ? { ...data } : {};

    // Add ops parameter if not already present
    if (!putData.ops) {
      putData.ops = this.getCurrentOps();
    }

    // Direct endpoint call
    const response = await this.client.put(url, putData);
    return this.transformResponse(response);
  }

  async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    // Direct endpoint call for DELETE requests
    const response = await this.client.delete(url);
    return this.transformResponse(response);
  }

  // Special method for WhatsApp API calls
  async postWhatsApp<T = any>(
    endpoint: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    const token = this.getAuthToken();
    const whatsappClient = axios.create({
      baseURL: config.whatsappApi,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const response = await whatsappClient.post(endpoint, data);
    return this.transformResponse(response);
  }

  private transformResponse<T>(response: AxiosResponse): ApiResponse<T> {
    // Handle different response structures from the backend
    if (response.data && response.data.data !== undefined) {
      return {
        data: response.data.data,
        success: true,
        message: response.data.message,
      };
    }

    return {
      data: response.data,
      success: true,
    };
  }
}

export const apiClient = new ApiClient();
